import axios from "axios";
import API_BASE_URL from "../utils/apiConfig";
import { notification } from "antd";
import {
  getBearerTokenFromHeaders,
  shouldClearSessionOnUnauthorized,
} from "../utils/authSession";

const PUBLIC_AUTH_PATHS = [
  "/auth/send-otp",
  "/auth/verify-otp",
  "/auth/magic-login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
];

export const createApiClient = (pathSuffix = "") => {
  const api = axios.create({
    baseURL: `${API_BASE_URL}${pathSuffix}`,
  });

  // no Authorization, no global redirect handling
  const isPublicAuthEndpoint = (url = "") => {
    try {
      const parsed = new URL(url, API_BASE_URL);
      const pathname = parsed.pathname || "";
      return PUBLIC_AUTH_PATHS.some((p) => pathname.startsWith(p));
    } catch (_) {
      return PUBLIC_AUTH_PATHS.some((p) => url.includes(p));
    }
  };

  api.interceptors.request.use(
    (config) => {
      const url = config?.url || "";
      if (!isPublicAuthEndpoint(url)) {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers = config.headers || {};
          if (!config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const requestUrl = error?.config?.url || "";
      const fromPublicAuthEndpoint = isPublicAuthEndpoint(requestUrl);
      const currentToken =
        typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const requestToken = getBearerTokenFromHeaders(error?.config?.headers);
      if (
        shouldClearSessionOnUnauthorized({
          status,
          isPublicAuthEndpoint: fromPublicAuthEndpoint,
          requestToken,
          currentToken,
          requestUrl,
          requestMethod: error?.config?.method,
        })
      ) {
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("selectedProject");
        } catch (_) {}

        // Redirect to login centrally
        if (typeof window !== "undefined") {
          // Avoid redirect loops
          const pathname = window.location.pathname || "";
          const isAlreadyOnLogin =
            pathname.startsWith("/login") ||
            pathname.startsWith("/otp") ||
            pathname.startsWith("/otp-login") ||
            pathname.startsWith("/magic-link") ||
            pathname.startsWith("/register");
          if (!isAlreadyOnLogin) {
            const returnPath =
              window.location.pathname +
              (window.location.search || "");
            window.location.replace(
              `/login?redirect=${encodeURIComponent(returnPath)}`
            );
          }
        }
      } else if (status === 403 && !fromPublicAuthEndpoint) {
        const serverMessage = error?.response?.data?.message;
        notification.error({
          message: "Access Denied",
          description:
            serverMessage || "You don't have permission to perform this action.",
        });
      }
      return Promise.reject(error);
    }
  );

  return api;
};

export default createApiClient;


